import { FC, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { formatCurrencyDecimals } from 'utils/formats';
import EmptyStateCard from 'modules/_shared/components/EmptyStates/EmptyStateCard';
import { Button } from 'react-bootstrap';
import { useEquityMarket } from '../../../contexts/EquityMarketContext';
import { RecentActivity } from '../../../types/EquityMarketContextType';
import { mockedRecentActivity } from '../../../mocks/mockedData';

type ActivityItemProps = {
  activity: RecentActivity;
  isLast: boolean;
};

const ActivityItem: FC<ActivityItemProps> = ({ activity, isLast }) => {
  const { t } = useTranslate();

  const isSell = activity.type === 'SELL';
  const iconClass = isSell ? 'ni-arrow-up-circle' : 'ni-arrow-down-circle';
  const colorClass = isSell
    ? 'bg-success-dim text-success'
    : 'bg-primary-dim text-primary';

  const description = isSell
    ? t('PartnerSoldShares', {
        count: activity.sharesCount,
        shareClass: activity.shareClass,
        price: formatCurrencyDecimals(activity.pricePerShare),
      })
    : t('PartnerBoughtShares', {
        count: activity.sharesCount,
        shareClass: activity.shareClass,
        price: formatCurrencyDecimals(activity.pricePerShare),
      });

  return (
    <div
      className={`timeline-item d-flex align-items-start ${
        !isLast ? 'pb-2' : ''
      }`}
    >
      <div
        className={`icon-circle icon-circle-sm ${colorClass} mr-3 flex-shrink-0`}
        style={{ marginTop: '2px' }}
      >
        <em className={`icon ni ${iconClass}`} />
      </div>
      <div className="flex-grow-1 border-bottom border-light pb-2">
        <span className="text-base">{description}</span>
        <div className="text-soft small mt-1">
          {activity.daysAgo} · {formatCurrencyDecimals(activity.totalAmount)}
        </div>
      </div>
    </div>
  );
};

const EquityMarketRecentActivity: FC = () => {
  const { t } = useTranslate();
  const { data } = useEquityMarket();

  const [isDemoDataVisible, setIsDemoDataVisible] = useState(false);

  const { isPartnerView } = data;

  if (isPartnerView) return null;

  const demoData = mockedRecentActivity;

  const handleToggleDemoData = () => {
    setIsDemoDataVisible(!isDemoDataVisible);
  };

  return (
    <div className="nk-block nk-block-spacing">
      <div className="card card-bordered">
        <div className="card-inner">
          <div className="nk-block-head-sm d-flex align-items-start justify-content-between">
            <div className="nk-block-head-content">
              <h5 className="nk-block-title">{t('RecentActivity')}</h5>
              <p className="text-soft mb-0">{t('LastCompletedTransactions')}</p>
            </div>

            <Button
              variant="link"
              className="link link-primary btn btn-link p-0"
              onClick={handleToggleDemoData}
            >
              {isDemoDataVisible ? t('HideDemo') : t('ViewDemo')}
            </Button>
          </div>

          {(isDemoDataVisible ? demoData : data.recentActivity).length > 0 ? (
            <div className="timeline equity-market-timeline mt-3">
              {(isDemoDataVisible ? demoData : data.recentActivity).map(
                (activity, index) => (
                  <ActivityItem
                    key={activity._id}
                    activity={activity}
                    isLast={index === data.recentActivity.length - 1}
                  />
                )
              )}
            </div>
          ) : (
            <EmptyStateCard
              mainMessage={t('NoTradingOffersCompleted')}
              bordered={false}
              centered
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default EquityMarketRecentActivity;
